import { Box, Button, Chip, OutlinedInput, TableCell, TableRow, Typography } from '@mui/material'
import React from 'react'
import { i18n } from '../../index/index'
import { monthName } from './MonthlyTargetsTable'
import { MonthlyTargetValue } from './types'
import { useValueFormat } from './valueFormat'

export interface MonthlyTargetRowProps {
  month: number
  monthlyTarget?: MonthlyTargetValue
  lastYearOutcome?: number
  hasLastYear: boolean
  isCurrentMonth: boolean
  isEditing: boolean
  unallocated: number
  isFocused: boolean
  onFocusMonth: (month: number | null) => void
  changeMonthlyTarget: (month: number, value: number) => void
  fillRemainderIntoMonth: (month: number) => void
}

/**
 * One month row in the vertical monthly table.
 * Columns: month name · last-year actual · editable target · Ero (delta vs last year).
 * The current month is highlighted. While editing, the "Lisää loput tähän" hint follows the
 * focused cell and appears only when the months are under-allocated (unallocated > 0);
 * clicking it adds the unallocated amount to this month.
 */
export function MonthlyTargetRow({
  month,
  monthlyTarget,
  lastYearOutcome,
  hasLastYear,
  isCurrentMonth,
  isEditing,
  unallocated,
  isFocused,
  onFocusMonth,
  changeMonthlyTarget,
  fillRemainderIntoMonth,
}: MonthlyTargetRowProps) {
  const { format, formatSigned } = useValueFormat()
  if (!monthlyTarget) {
    return (
      <TableRow>
        <TableCell>{capitalize(monthName(month))}</TableCell>
        <TableCell align='right'>
          {hasLastYear && lastYearOutcome !== undefined ? format(lastYearOutcome) : '-'}
        </TableCell>
        <TableCell align='right'>-</TableCell>
        <TableCell align='right'>-</TableCell>
      </TableRow>
    )
  }

  const target = monthlyTarget.value
  // No comparison ("-") when there is no actual for the month (e.g. already cleaned up)
  // or no target has been set yet (0).
  const delta = hasLastYear && lastYearOutcome !== undefined && target > 0 ? target - lastYearOutcome : undefined
  const showHint = isFocused && unallocated > 0

  return (
    <TableRow
      sx={
        isCurrentMonth
          ? {
              backgroundColor: (theme) => theme.palette.currentMonth.tint,
              '& > td': { borderTop: (theme) => `1px solid ${theme.palette.divider}` },
            }
          : undefined
      }
    >
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {capitalize(monthName(month))}
          {isCurrentMonth && (
            <Chip label={i18n.t('admin.monthlyTargets.currentMonthChip')} size='small' color='success' />
          )}
        </Box>
      </TableCell>
      <TableCell align='right'>
        {hasLastYear && lastYearOutcome !== undefined ? format(lastYearOutcome) : '-'}
      </TableCell>
      <TableCell align='right'>
        {isEditing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <OutlinedInput
              size='small'
              type='number'
              value={target || ''}
              onFocus={() => onFocusMonth(month)}
              onBlur={() => onFocusMonth(null)}
              onChange={(event) => changeMonthlyTarget(month, Number(event.target.value) || 0)}
              sx={{
                fontSize: '0.75rem',
                width: 110,
                ...(isCurrentMonth ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: 'success.main' } } : {}),
              }}
              inputProps={{ style: { textAlign: 'right' } }}
            />
            {showHint && (
              <Button
                type='button'
                variant='outlined'
                color='success'
                size='small'
                // onMouseDown (not onClick) so the input's onBlur doesn't hide the hint
                // before the click registers.
                onMouseDown={(event) => {
                  event.preventDefault()
                  fillRemainderIntoMonth(month)
                }}
                sx={{ mt: 0.5, borderRadius: 5, textTransform: 'none', fontSize: '0.7rem', py: 0.1 }}
              >
                {i18n.t('admin.monthlyTargets.addRemainderHere', { remainder: format(unallocated) })}
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minHeight: 40 }}>
            {format(target)}
          </Box>
        )}
      </TableCell>
      <TableCell align='right'>
        {delta === undefined ? (
          '-'
        ) : (
          <Typography variant='body2' color={delta >= 0 ? 'success.main' : 'error.main'}>
            {formatSigned(delta)}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  )
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1)
}
